import type { SavedFurnace } from "../core/save";
import { makeStack, stackLimit, type ItemStack } from "../world/items";
import {
  findSmeltingRecipe,
  fuelValue,
  SMELT_SECONDS,
  type SmeltingRecipe,
} from "../world/smelting";
import { clickStack, type Inventory } from "./inventory";

/**
 * Per-block furnace state and the session-wide registry.
 *
 * Slots are input, fuel and output. Fuel is burned one item at a time and each
 * item provides a fixed number of smelts, so a coal is never wasted if the
 * input is removed halfway through. The registry key is the block position, so
 * a furnace keeps working when its screen is closed and can be reopened.
 */

export const FURNACE_INPUT = 0;
export const FURNACE_FUEL = 1;
export const FURNACE_OUTPUT = 2;

export class FurnaceState {
  readonly slots: (ItemStack | null)[] = [null, null, null];

  /** Current progress toward the next item, 0..1. */
  progress = 0;
  /** Smelts left from the currently burning fuel item. */
  burnRemaining = 0;
  /** Smelts the current fuel item provides, for the flame meter. */
  burnTotal = 0;

  get lit(): boolean {
    return this.burnRemaining > 0;
  }

  get output(): ItemStack | null {
    return this.slots[FURNACE_OUTPUT];
  }

  slotAt(index: number): ItemStack | null {
    return this.slots[index] ?? null;
  }

  /** Advance the furnace; returns true when an item finishes smelting. */
  update(dt: number): boolean {
    if (!Number.isFinite(dt) || dt <= 0) return false;

    const input = this.slots[FURNACE_INPUT];
    const recipe = input ? findSmeltingRecipe(input.id) : null;
    if (!recipe) {
      this.progress = 0;
      return false;
    }
    if (!this.canAccept(recipe)) return false;

    if (this.burnRemaining <= 0 && !this.consumeFuel()) {
      this.progress = 0;
      return false;
    }

    this.progress += dt / SMELT_SECONDS;
    let produced = false;

    while (this.progress >= 1) {
      const current = this.slots[FURNACE_INPUT];
      const currentRecipe = current ? findSmeltingRecipe(current.id) : null;
      if (!current || !currentRecipe || !this.canAccept(currentRecipe)) {
        this.progress = 0;
        break;
      }
      if (this.burnRemaining <= 0 && !this.consumeFuel()) {
        this.progress = 0;
        break;
      }

      this.slots[FURNACE_INPUT] =
        current.count > 1 ? { ...current, count: current.count - 1 } : null;
      this.addOutput(currentRecipe);
      this.burnRemaining--;
      this.progress -= 1;
      produced = true;
    }

    return produced;
  }

  /**
   * Apply the shared inventory click rules to a furnace slot, with the input
   * and fuel filters and an output slot that can only be taken from.
   */
  clickSlot(
    index: number,
    button: "left" | "right",
    held: ItemStack | null,
  ): ItemStack | null {
    if (index === FURNACE_OUTPUT) {
      // The output is take-only: never let a held stack fall into it.
      if (held) return held;
      return clickStack(this.slots, index, button, null);
    }

    if (index === FURNACE_INPUT && held && !findSmeltingRecipe(held.id)) {
      return held;
    }
    if (index === FURNACE_FUEL && held && fuelValue(held.id) <= 0) {
      return held;
    }
    return clickStack(this.slots, index, button, held);
  }

  /** Shift-click one of the three slots back into the player's pack. */
  quickMove(index: number, inventory: Inventory): boolean {
    const stack = this.slots[index] ?? null;
    if (!stack) return false;
    this.slots[index] = null;
    inventory.addStack(stack);
    return true;
  }

  /**
   * Shift-click a pack stack into the furnace. Smeltables go to the input,
   * fuels to the fuel slot, and anything else is refused. Returns how many
   * items moved.
   */
  insert(stack: ItemStack): number {
    const index = findSmeltingRecipe(stack.id)
      ? FURNACE_INPUT
      : fuelValue(stack.id) > 0
        ? FURNACE_FUEL
        : -1;
    if (index < 0) return 0;

    const target = this.slots[index];
    if (!target) {
      const moved = Math.min(stack.count, stackLimit(stack.id));
      this.slots[index] = { ...stack, count: moved };
      return moved;
    }
    if (target.id !== stack.id) return 0;

    const room = stackLimit(target.id) - target.count;
    const moved = Math.min(room, stack.count);
    if (moved > 0) {
      this.slots[index] = { ...target, count: target.count + moved };
    }
    return moved;
  }

  /** Remove and return every stack, used when the furnace block is broken. */
  drain(): ItemStack[] {
    const out: ItemStack[] = [];
    for (let i = 0; i < this.slots.length; i++) {
      const stack = this.slots[i];
      if (stack) out.push(stack);
      this.slots[i] = null;
    }
    this.progress = 0;
    this.burnRemaining = 0;
    this.burnTotal = 0;
    return out;
  }

  private canAccept(recipe: SmeltingRecipe): boolean {
    const output = this.slots[FURNACE_OUTPUT];
    if (!output) return true;
    return (
      output.id === recipe.result &&
      output.count + recipe.count <= stackLimit(recipe.result)
    );
  }

  private consumeFuel(): boolean {
    const fuel = this.slots[FURNACE_FUEL];
    if (!fuel) return false;

    const value = fuelValue(fuel.id);
    if (value <= 0) return false;

    this.slots[FURNACE_FUEL] =
      fuel.count > 1 ? { ...fuel, count: fuel.count - 1 } : null;
    this.burnTotal = value;
    this.burnRemaining = value;
    return true;
  }

  private addOutput(recipe: SmeltingRecipe): void {
    const output = this.slots[FURNACE_OUTPUT];
    if (!output) {
      this.slots[FURNACE_OUTPUT] = makeStack(recipe.result, recipe.count);
      return;
    }
    this.slots[FURNACE_OUTPUT] = {
      ...output,
      count: output.count + recipe.count,
    };
  }
}

/** Furnace states for the current world, keyed by block position. */
export class FurnaceBook {
  private readonly states = new Map<string, FurnaceState>();

  at(x: number, y: number, z: number): FurnaceState {
    const key = `${x},${y},${z}`;
    let furnace = this.states.get(key);
    if (!furnace) {
      furnace = new FurnaceState();
      this.states.set(key, furnace);
    }
    return furnace;
  }

  remove(x: number, y: number, z: number): FurnaceState | null {
    const key = `${x},${y},${z}`;
    const furnace = this.states.get(key) ?? null;
    this.states.delete(key);
    return furnace;
  }

  update(dt: number): void {
    for (const furnace of this.states.values()) furnace.update(dt);
  }

  /** States with items or burn progress, ready for the save file. */
  snapshot(): SavedFurnace[] {
    const out: SavedFurnace[] = [];
    for (const [key, furnace] of this.states) {
      const empty =
        furnace.slots.every((stack) => stack === null) &&
        furnace.progress === 0 &&
        furnace.burnRemaining === 0 &&
        furnace.burnTotal === 0;
      if (empty) continue;
      const [x, y, z] = key.split(",").map(Number);
      out.push({
        x,
        y,
        z,
        slots: furnace.slots.map((stack) => (stack ? { ...stack } : null)),
        progress: furnace.progress,
        burnRemaining: furnace.burnRemaining,
        burnTotal: furnace.burnTotal,
      });
    }
    return out;
  }

  /** Replace every furnace with its saved contents and burn state. */
  restore(entries: readonly SavedFurnace[]): void {
    this.states.clear();
    for (const entry of entries) {
      const furnace = this.at(entry.x, entry.y, entry.z);
      for (let i = 0; i < furnace.slots.length; i++) {
        const stack = entry.slots[i] ?? null;
        furnace.slots[i] = stack ? { ...stack } : null;
      }
      furnace.progress = entry.progress;
      furnace.burnRemaining = entry.burnRemaining;
      furnace.burnTotal = entry.burnTotal;
    }
  }

  clear(): void {
    this.states.clear();
  }
}
